import { useEffect, useState } from "react";
import { Button } from "@workspace/amarmeds-ds/components/ui/button";

import { detectPlatform, installEvent, installSteps, onInstallChange, promptInstall, runningInstalled, snooze, snoozedUntil, type Role } from "./install";

// "Install the SAVON app" on every device until it is installed: an Install button
// where the browser can open its own dialog (Chrome, Edge, Samsung Internet,
// Opera on Android and computers), and the exact steps everywhere else (Safari
// on iPhone, iPad and Mac, Firefox, in-app browsers). "Later" hides it for a day.

const NAMES: Record<Role | "staff", { en: string; bn: string }> = {
  staff: { en: "SAVON Staff", bn: "SAVON স্টাফ" },
  ops: { en: "SAVON Ops", bn: "SAVON অপস" },
  pharmacy: { en: "SAVON Pharmacy", bn: "SAVON ফার্মেসি" },
  rider: { en: "SAVON Rider", bn: "SAVON রাইডার" },
};

export function InstallPrompt({ role, ignoreSnooze = false }: { role: Role | null; ignoreSnooze?: boolean }) {
  const [, rerender] = useState(0);
  const [hidden, setHidden] = useState(() => runningInstalled() || (!ignoreSnooze && snoozedUntil() > Date.now()));
  const [copied, setCopied] = useState(false);
  useEffect(() => onInstallChange(() => rerender((n) => n + 1)), []);
  useEffect(() => {
    // Opened from the home screen after installing in this tab's lifetime.
    const mq = window.matchMedia?.("(display-mode: standalone)");
    const onChange = () => mq?.matches && setHidden(true);
    mq?.addEventListener?.("change", onChange);
    return () => mq?.removeEventListener?.("change", onChange);
  }, []);
  if (hidden || runningInstalled()) return null;

  const platform = detectPlatform(navigator.userAgent, navigator.maxTouchPoints);
  const canPrompt = !!installEvent();
  const steps = installSteps(platform);
  const name = NAMES[role ?? "staff"];
  const bnFirst = role === "rider";
  const title = bnFirst ? `${name.bn} অ্যাপ ইনস্টল করুন` : `Install the ${name.en} app`;
  const subtitle = bnFirst ? `Install the ${name.en} app` : `${name.bn} অ্যাপ ইনস্টল করুন`;
  const why = bnFirst
    ? "হোম স্ক্রিন থেকে এক চাপে খুলুন, পুরো স্ক্রিনে, নতুন রানের নোটিফিকেশনসহ।"
    : "Opens in one tap from your home screen or dock, full screen, with new-order notifications.";

  const later = () => {
    snooze();
    setHidden(true);
  };
  const install = async () => {
    if (await promptInstall()) setHidden(true);
  };
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(location.origin);
      setCopied(true);
    } catch {
      window.prompt(bnFirst ? "লিংকটি কপি করুন" : "Copy this link", location.origin);
    }
  };

  return (
    <div role="region" aria-label={title} className="no-print border-b bg-primary/10 px-3 py-3 sm:px-4" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
      <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-3">
        <img src="/icon-192.png" alt="" width={44} height={44} className="shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1 basis-56">
          <div className="font-semibold">{title}</div>
          <div className="text-xs text-muted-foreground">{subtitle}</div>
          <div className="mt-1 text-sm">{canPrompt ? why : bnFirst ? steps.bn : steps.en}</div>
          {!canPrompt && <div className="text-xs text-muted-foreground">{bnFirst ? steps.en : steps.bn}</div>}
        </div>
        <div className="flex shrink-0 gap-2">
          {canPrompt && (
            <Button className="min-h-11" onClick={() => void install()}>
              {bnFirst ? "ইনস্টল করুন" : "Install"}
            </Button>
          )}
          {platform === "in-app" && (
            <Button className="min-h-11" onClick={() => void copy()}>
              {copied ? (bnFirst ? "কপি হয়েছে" : "Copied") : bnFirst ? "লিংক কপি" : "Copy link"}
            </Button>
          )}
          <Button variant="ghost" className="min-h-11" onClick={later}>
            {bnFirst ? "পরে" : "Later"}
          </Button>
        </div>
      </div>
    </div>
  );
}
